import { ReactNode, useMemo, useRef, useState, type CSSProperties, type Dispatch, type SetStateAction } from "react";
import { Button, Checkbox, Dropdown, Menu } from "antd";
import clsx from "clsx";
import { PlusIconBlack } from "@/shared/assets/svg/icons";
import { SelectedTag } from "./ui/selected-tag";
import "./input-select-additional.scss";

export type IStatusAdditional = {
  id: string;
  status: string;
  icon?: ReactNode;
  count?: number;
  checked?: boolean;
};

interface InputSelectAdditionalProps {
  title: string;
  statuses?: IStatusAdditional[];
  style?: CSSProperties;
  theme?: "default" | "transparent";

  selectedStatus?: IStatusAdditional | null;
  setSelectedStatus?: (status: IStatusAdditional | null) => void;

  isMultiple?: boolean;
  selectedStatuses?: string[];
  setSelectedStatuses?: Dispatch<SetStateAction<string[]>>;

  showAllOption?: boolean;
  allOptionLabel?: string;

  renderMenuItem?: (args: { status: IStatusAdditional; checked: boolean }) => ReactNode;
  renderSelectedItem?: (args: { status: IStatusAdditional; onRemove: () => void }) => ReactNode;
}

export const InputSelectAdditional = ({
  title,
  statuses = [],
  style,
  theme = "default",
  selectedStatus,
  setSelectedStatus,
  isMultiple = false,
  selectedStatuses = [],
  setSelectedStatuses,
  showAllOption = false,
  allOptionLabel = "Все",
  renderMenuItem,
  renderSelectedItem,
}: InputSelectAdditionalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuMode, setMenuMode] = useState<"options" | "selected">("options");
  const suppressCloseRef = useRef(false);

  const selectedItems = useMemo(
    () => statuses.filter((s) => selectedStatuses.includes(s.id)),
    [selectedStatuses, statuses],
  );

  const isAllChecked = statuses.length > 0 && selectedStatuses.length === statuses.length;
  const isAllIndeterminate = selectedStatuses.length > 0 && selectedStatuses.length < statuses.length;
  const hasSelection = isMultiple ? selectedStatuses.length > 0 : Boolean(selectedStatus);
  const isTransparent = theme === "transparent";

  const keepOpen = (e?: { stopPropagation?: () => void; preventDefault?: () => void }) => {
    suppressCloseRef.current = true;
    e?.stopPropagation?.();
    e?.preventDefault?.();
  };

  const removeById = (id: string) =>
    setSelectedStatuses?.(selectedStatuses.filter((v) => v !== id));

  const toggleAll = () => {
    setSelectedStatuses?.(isAllChecked ? [] : statuses.map((s) => s.id));
    setIsOpen(true);
  };

  const toggleSingle = (status: IStatusAdditional) => {
    if (isMultiple) {
      const next = selectedStatuses.includes(status.id)
        ? selectedStatuses.filter((id) => id !== status.id)
        : [...selectedStatuses, status.id];
      setSelectedStatuses?.(next);
      setIsOpen(true);
      return;
    }

    setSelectedStatus?.(status);
    setIsOpen(false);
  };

  const clearSingle = () => setSelectedStatus?.(null);

  const handleDropdownVisibility = (next: boolean) => {
    if (!next && suppressCloseRef.current) {
      suppressCloseRef.current = false;
      return;
    }
    suppressCloseRef.current = false;
    if (!next) setMenuMode("options");
    setIsOpen(next);
  };

  const renderTag = (status: IStatusAdditional, onRemove: () => void) =>
    renderSelectedItem
      ? renderSelectedItem({ status, onRemove })
      : <SelectedTag label={status.status} onRemove={onRemove} />;

  const optionsMenu = (
    <Menu className="isa-menu">
      {isMultiple && showAllOption && (
        <Menu.Item
          key="__all__"
          className="isa-menu__item"
          onClick={(info) => { keepOpen(info.domEvent); toggleAll(); }}
        >
          <div className="isa-menu__row">
            <Checkbox
              checked={isAllChecked}
              indeterminate={isAllIndeterminate}
              onClick={(e) => { keepOpen(e); toggleAll(); }}
            />
            <span className="isa-menu__label">{allOptionLabel}</span>
          </div>
        </Menu.Item>
      )}

      {statuses.map((status) => {
        const checked = isMultiple
          ? selectedStatuses.includes(status.id)
          : selectedStatus?.id === status.id;

        return (
          <Menu.Item
            key={status.id}
            className="isa-menu__item"
            onClick={(info) => {
              if (isMultiple) keepOpen(info.domEvent);
              toggleSingle(status);
            }}
          >
            <div className="isa-menu__row">
              <Checkbox
                checked={checked}
                onClick={(e) => {
                  if (isMultiple) keepOpen(e);
                  else e.stopPropagation();
                  toggleSingle(status);
                }}
              />
              {renderMenuItem ? (
                <span className="isa-menu__custom">
                  {renderMenuItem({ status, checked })}
                </span>
              ) : (
                <span className="isa-menu__label">{status.status}</span>
              )}
            </div>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  const selectedDropdownMenu = (
    <Menu className="isa-selected-menu">
      {selectedItems.map((status) => (
        <Menu.Item key={status.id} className="isa-selected-menu__item">
          {renderTag(status, () => removeById(status.id))}
        </Menu.Item>
      ))}
    </Menu>
  );

  const renderMultipleContent = () => {
    if (selectedItems.length === 0) {
      return <PlusIconBlack className="isa-icon" />;
    }

    const first = selectedItems[0];
    const rest = selectedItems.length - 1;

    return (
      <div className="isa-tags">
        {renderTag(first, () => removeById(first.id))}

        {rest > 0 && (
          <button
            type="button"
            className="isa-count-tag"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setMenuMode("selected");
              setIsOpen(true);
            }}
          >
            +{rest} ...
          </button>
        )}
      </div>
    );
  };

  const renderSingleContent = () => {
    if (!selectedStatus) {
      return <PlusIconBlack className="isa-icon" />;
    }

    return (
      <div className="isa-tags">
        {renderTag(selectedStatus, clearSingle)}
      </div>
    );
  };

  return (
    <Dropdown
      overlay={menuMode === "selected" ? selectedDropdownMenu : optionsMenu}
      trigger={["click"]}
      onVisibleChange={handleDropdownVisibility}
      placement="bottomLeft"
      visible={isOpen}
      overlayStyle={{ minWidth: "fit-content" }}
    >
      <Button
        className={clsx("isa", {
          "isa--selected": hasSelection,
          "isa--transparent": isTransparent,
          "isa--transparent-selected": isTransparent && hasSelection,
          "isa--transparent-empty": isTransparent && !hasSelection,
        })}
        style={style}
      >
        <span className="isa__title">{title}</span>
        {isMultiple ? renderMultipleContent() : renderSingleContent()}
      </Button>
    </Dropdown>
  );
};
